import React, { useState, useReducer, useContext } from "react";
import axios from "axios";
import clsx from "clsx";
import PropTypes from "prop-types";
import { emailRegex } from "../../helpers/constants";
import useTranslations from "../../hoc/useTranslations";
import NewsletterInput from "./NewsletterInput";
import Button from "../Button/Button";
import Loader from "../Loader/Loader";

import SuccessSVG from "../../assets/icons/success.svg";
import "./NewsletterForm.scss";
import { LocaleContext } from "../Layout/Layout";
import { trackUser } from "../../helpers/helpers";

const NewsletterForm = ({ lighten }) => {
	const { locale } = useContext(LocaleContext);

	const [values, setValues] = useReducer((state, newState) => ({ ...state, ...newState }), {
		email: "",
		checkbox: false,
	});

	const [errors, setErrors] = useReducer((state, newState) => ({ ...state, ...newState }), {
		email: false,
		checkbox: false,
	});

	const [emtpyData, setEmptyData] = useReducer((state, newState) => ({ ...state, ...newState }), {
		email: true,
	});

	const [emailSent, setEmailSent] = useState(false);
	const [isSending, setIsSending] = useState(false);

	const handleInputChange = e => {
		const { value } = e.target;
		const { name: type } = e.target;
		const { checked } = e.target;

		switch (type) {
			case "email":
				setErrors({ [type]: !value.match(emailRegex) });
				setEmptyData({ [type]: value === "" });
				return setValues({ [type]: value });
			case "checkbox":
				setErrors({ [type]: !checked });
				return setValues({ [type]: checked });
			default:
				return;
		}
	};

	const validateForm = () => {
		let result = true;

		if (!values.email.match(emailRegex)) {
			setErrors({ email: true });
			result = false;
		}

		if (!values.checkbox) {
			setErrors({ checkbox: true });
			result = false;
		}

		return result;
	};

	const handleBlur = () => {
		validateForm();
	};

	const submitForm = async e => {
		e.preventDefault();
		if (!validateForm()) return;

		setIsSending(true);

		try {
			await axios({
				method: "post",
				url: process.env.GATSBY_MAILCHIMP_URL,
				params: {
					EMAIL: values.email,
					LANGUAGE: locale,
				},
				data: {},
				headers: {
					"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
				},
			});
		} catch (err) {
			throw new Error(
				err &&
					`User just tried to get contact with Botwise: user email: ${values.email}
				error: ${JSON.stringify(err)}
				`,
			);
		} finally {
			setIsSending(false);
			setEmailSent(true);
			trackUser({
				tagManager: true,
				eventName: "trybotwise",
			});
		}
	};

	const {
		form: { success_message },
		newsletter: { acceptance, button, placeholder_email },
	} = useTranslations();

	return (
		<>
			{!emailSent ? (
				<form
					className={clsx(
						"newsletterForm",
						lighten && "newsletterForm--lighten",
						errors.checkbox && "newsletterForm__invalid--checkbox",
						errors.email && "newsletterForm__invalid--email",
						emtpyData.email && "newsletterForm__emptyData",
					)}
					onSubmit={submitForm}
				>
					<div className="newsletterForm__container">
						<NewsletterInput
							type="email"
							name="email"
							placeholder={placeholder_email}
							value={values.email}
							handleBlur={handleBlur}
							handleChange={handleInputChange}
						/>
						<Button
							buttonClass="inverse"
							type="submit"
							onClick={submitForm}
							disabled={errors.email || errors.checkbox}
						>
							{button}
						</Button>
					</div>
					<NewsletterInput
						type="checkbox"
						name="checkbox"
						placeholder={acceptance}
						value={values.checkbox}
						handleBlur={handleBlur}
						handleChange={handleInputChange}
					/>
					{isSending && <Loader style={{ height: "150%" }} />}
				</form>
			) : (
				<p className="email-sent">
					<SuccessSVG className="email-sent__icon" />
					{success_message}
				</p>
			)}
		</>
	);
};

NewsletterForm.propTypes = {
	lighten: PropTypes.bool,
};

NewsletterForm.defaultProps = {
	lighten: false,
};

export default NewsletterForm;
